import { cn } from "@/lib/utils";
import { Check, X, Minus } from "lucide-react";

interface ComparisonColumn {
  title: string;
  highlighted?: boolean;
  values: (boolean | string | null)[]; // true = check, false = x, null = dash, string = text
}

interface ComparisonBlockProps {
  rows: string[];
  columns: ComparisonColumn[];
  className?: string;
  tableClassName?: string;
  headerCellClassName?: string;
  rowLabelClassName?: string;
  cellClassName?: string;
}

const CellValue = ({ value }: { value: boolean | string | null }) => {
  if (value === true)
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/15" role="img" aria-label="Included">
        <Check className="w-4 h-4 text-accent" aria-hidden="true" />
      </span>
    );
  if (value === false)
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-destructive/10" role="img" aria-label="Not included">
        <X className="w-4 h-4 text-destructive/70" aria-hidden="true" />
      </span>
    );
  if (value === null) return <span role="img" aria-label="Not applicable"><Minus className="w-4 h-4 text-muted-foreground/30 mx-auto" aria-hidden="true" /></span>;
  return <span className="text-sm md:text-base text-foreground font-medium">{value}</span>;
};

const ComparisonBlock = ({
  rows,
  columns,
  className,
  tableClassName,
  headerCellClassName,
  rowLabelClassName,
  cellClassName,
}: ComparisonBlockProps) => (
  <div className={cn("overflow-x-auto border border-border bg-card shadow-sm", className)}>
    <table className={cn("w-full border-collapse", tableClassName)}>
      <thead>
        <tr>
          <th
            className={cn(
              "w-[40%] border-b-2 border-border p-3 text-left font-body text-sm uppercase tracking-[0.14em] text-muted-foreground sm:p-5",
              rowLabelClassName
            )}
          />
          {columns.map((col, ci) => (
            <th
              key={col.title}
              className={cn(
                "min-w-[120px] border-b-2 border-border p-3 text-center font-headline text-lg sm:min-w-[160px] sm:p-5 sm:text-xl",
                ci > 0 && "border-l border-border",
                col.highlighted ? "bg-accent/10 text-foreground" : "text-foreground",
                headerCellClassName
              )}
            >
              {col.title}
              {col.highlighted && (
                <span className="block font-body text-xs uppercase tracking-[0.14em] text-accent mt-1.5 font-semibold">
                  ★ Recommended
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={row}
            className={cn(
              "border-b border-border transition-colors hover:bg-muted/30 last:border-b-0",
              i % 2 === 1 && "bg-muted/10"
            )}
          >
            <td
              className={cn(
                "p-3 text-left font-body text-sm font-medium text-foreground sm:p-5 md:text-base",
                rowLabelClassName
              )}
            >
              {row}
            </td>
            {columns.map((col, ci) => (
              <td
                key={`${col.title}-${i}`}
                className={cn(
                  "p-3 text-center sm:p-5",
                  ci > 0 && "border-l border-border",
                  col.highlighted && "bg-accent/5",
                  cellClassName
                )}
              >
                <CellValue value={col.values[i]} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ComparisonBlock;
